import React from 'react';
import { Button } from '@mui/material';
import { MuiDialog } from './MuiDialog';
import { useConfirmDialog, ConfirmContextType } from '@/hooks/useDialogs';
import { ConfirmContext } from '@/hooks/useConfirm';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  variant?: 'danger' | 'warning' | 'info';
}

/**
 * Confirmation dialog using native MUI components
 * Replaces AlertDialog from shadcn
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  variant = 'info',
}) => {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const getConfirmButtonColor = () => {
    switch (variant) {
      case 'danger':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'primary';
    }
  };

  const actions = (
    <>
      <Button onClick={onClose} variant="outlined">
        {cancelText}
      </Button>
      <Button 
        onClick={handleConfirm} 
        variant="contained" 
        color={getConfirmButtonColor()}
        sx={{ ml: 1 }}
      >
        {confirmText}
      </Button>
    </>
  );

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      title={title}
      description={message}
      actions={actions}
      maxWidth="sm"
      fullWidth
    />
  );
};

/**
 * Provider for confirmation dialogs throughout the app
 */
export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { open, showConfirm, handleConfirm, handleCancel, config, setOpen } = useConfirmDialog();

  const showConfirmDialog = React.useCallback((newConfig: Parameters<ConfirmContextType['showConfirm']>[0]) => {
    showConfirm({
      ...newConfig,
      onConfirm: newConfig.onConfirm,
    });
  }, [showConfirm]);

  return (
    <ConfirmContext.Provider value={{ showConfirm: showConfirmDialog }}>
      {children}
      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={handleConfirm}
        {...config}
      />
    </ConfirmContext.Provider>
  );
};
